"use client";

import { Building2, Check, Rocket, StarIcon, Zap } from "lucide-react";
import { motion } from "motion/react";
import { useRouter } from "next/navigation";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  FadeInUp,
  StaggerContainer,
  StaggerItem,
} from "@/components/ui/motion";
import { FinalCTA } from "../components/FinalCTA";

const plans = [
  {
    name: "Free",
    icon: Rocket,
    price: "$0",
    period: "/month",
    description: "Perfect for side projects and experimentation",
    features: [
      "1,000 runs/month",
      "5 active workflows",
      "7-day execution history",
      "Community support",
      "Public templates",
    ],
    cta: "Start free",
    popular: false,
  },
  {
    name: "Pro",
    icon: Zap,
    price: "$29",
    period: "/month",
    description: "For growing teams and serious automation",
    features: [
      "50,000 runs/month",
      "Unlimited workflows",
      "30-day execution history",
      "Priority support",
      "Custom webhooks",
      "Team collaboration (5 seats)",
      "Advanced analytics",
    ],
    cta: "Start free trial",
    popular: true,
  },
  {
    name: "Enterprise",
    icon: Building2,
    price: "$49",
    period: "/month",
    description: "For large organizations with advanced needs",
    features: [
      "Unlimited runs",
      "Unlimited workflows",
      "Unlimited history",
      "Dedicated support",
      "SSO & SAML",
      "Custom contracts",
      "SLA guarantee",
      "On-premise option",
    ],
    cta: "Start free trial",
    popular: false,
  },
];

const pricingFAQ = [
  {
    q: "What counts as a workflow run?",
    a: "A workflow run is counted each time your workflow executes from trigger to completion. Retries within a run don't count as additional runs.",
  },
  {
    q: "Can I change plans anytime?",
    a: "Yes, you can upgrade or downgrade anytime. Upgrades take effect immediately. Downgrades apply at your next billing cycle.",
  },
  {
    q: "What happens if I exceed my run limit?",
    a: "We'll notify you when you reach 80% of your limit. If you exceed it, workflows will pause until the next billing cycle or you upgrade.",
  },
  {
    q: "Do you offer annual billing?",
    a: "Yes! Annual billing saves you 20%. Contact us for annual plans.",
  },
];

const PricingPage = () => {
  const navigate = useRouter();

  const handleGiftCheckout = async () => {
    try {
      const { authClient } = await import("@/lib/auth-client");
      const session = await authClient.getSession();

      if (!session.data) {
        // Redirect to login with proper return URL for gifting
        const returnUrl = encodeURIComponent(
          "/settings?tab=billing&intent=gift",
        );
        navigate.push(`/login?callbackURL=${returnUrl}`);
        return;
      }

      await authClient.checkout({
        slug: "gift",
      });
    } catch (error) {
      console.error(error);
      window.location.href = "/login";
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-16">
        {/* Hero */}
        <section className="py-16 lg:py-24 relative overflow-hidden">
          {/* Enhanced background decoration */}
          <div className="absolute top-10 left-1/4 w-[600px] h-[600px] bg-primary/12 rounded-full blur-[140px] pointer-events-none" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-accent/15 rounded-full blur-[120px] pointer-events-none" />
          <div className="absolute top-1/2 right-1/3 w-[350px] h-[350px] bg-success/8 rounded-full blur-[100px] pointer-events-none" />

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
            <FadeInUp className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-4">
                Simple, <span className="gradient-text">transparent</span>{" "}
                pricing
              </h1>
              <p className="text-lg text-muted-foreground">
                Start free, scale as you grow. No hidden fees, no surprises.
              </p>
            </FadeInUp>
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="pb-20 pt-5 section-gradient relative">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <StaggerContainer className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {plans.map((plan) => (
                <StaggerItem key={plan.name}>
                  <motion.div
                    whileHover={{ y: -4 }}
                    className={`relative p-8 rounded-2xl border transition-all h-full flex flex-col ${
                      plan.popular
                        ? "bg-card border-primary/50 shadow-xl shadow-primary/10"
                        : "bg-card/50 border-border hover:border-muted-foreground/30"
                    }`}
                  >
                    {plan.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="px-4 py-1.5 text-xs font-semibold bg-primary text-primary-foreground rounded-full">
                          Most popular
                        </span>
                      </div>
                    )}

                    <div className="flex items-center gap-3 mb-4">
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          plan.popular ? "bg-primary/20" : "bg-muted"
                        }`}
                      >
                        <plan.icon
                          className={`w-5 h-5 ${plan.popular ? "text-primary" : "text-muted-foreground"}`}
                        />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-foreground">
                          {plan.name}
                        </h3>
                      </div>
                    </div>

                    <p className="text-sm text-muted-foreground mb-6">
                      {plan.description}
                    </p>

                    <div className="mb-6">
                      <span className="text-4xl font-bold text-foreground">
                        {plan.price}
                      </span>
                      <span className="text-muted-foreground">
                        {plan.period}
                      </span>
                    </div>

                    <ul className="space-y-3 mb-8 flex-grow">
                      {plan.features.map((feature) => (
                        <li
                          key={feature}
                          className="flex items-start gap-3 text-sm text-muted-foreground"
                        >
                          <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                          {feature}
                        </li>
                      ))}
                    </ul>

                    <Button
                      variant={plan.popular ? "default" : "outline"}
                      className="w-full"
                      size="lg"
                      onClick={() =>
                        navigate.push(
                          plan.name === "Enterprise" ? "/contact" : "/register",
                        )
                      }
                    >
                      {plan.cta}
                    </Button>
                  </motion.div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* Sponsor Section */}
        <section className="py-12 container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary/10 via-background to-accent/10 border border-primary/20 p-8 sm:p-12 text-center">
            <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-primary/20 rounded-full blur-3xl pointer-events-none" />

            <FadeInUp>
              <div className="flex justify-center mb-6">
                <div className="p-3 bg-primary/10 rounded-2xl">
                  <StarIcon className="w-8 h-8 text-primary" />
                </div>
              </div>
              <h2 className="text-3xl text-white font-bold mb-4">
                Support our Work
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-8">
                Love what we're building? You can support the development of
                Makeplans by gifting a subscription. Your contribution directly
                creates more features, better docs, and a faster platform.
              </p>
              <Button
                size="lg"
                className="bg-gradient-to-r from-primary/75 to-primary/85 hover:from-primary/90 hover:to-primary/90 text-white border-none shadow-lg shadow-primary/20"
                onClick={handleGiftCheckout}
              >
                Gift a Subscription
              </Button>
            </FadeInUp>
          </div>
        </section>

        {/* Pricing FAQ */}
        <section className="py-20 section-feature relative">
          <div className="absolute inset-0 pattern-dots opacity-20 pointer-events-none" />
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
            <FadeInUp className="text-center mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">
                Pricing questions
              </h2>
            </FadeInUp>

            <FadeInUp delay={0.1}>
              <Accordion type="single" collapsible className="space-y-3">
                {pricingFAQ.map((item, index) => (
                  <AccordionItem
                    key={index}
                    value={`item-${index}`}
                    className="bg-card border border-border rounded-xl px-6 data-[state=open]:border-primary/50"
                  >
                    <AccordionTrigger className="text-left hover:no-underline py-5">
                      <span className="font-medium text-foreground">
                        {item.q}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground pb-5">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </FadeInUp>
          </div>
        </section>

        <FinalCTA />
      </main>
    </div>
  );
};

export default PricingPage;
